<script lang="ts">
import GlpCoinSilver from '@/assets/images/glp-coin-silver.png'
import GlpCoin from '@/assets/images/glp-coin.png'
import { useCurrencyFormatter } from '@/composables/useCurrencyFormatter'
import type { Deposit, DepositBonusOption } from '@/types'
import {
  calculateBonusCredit,
  calculateWalletCoins,
  getBonusTierForAmount,
} from '@/utils/deposit-utils'
import { type PropType, defineComponent } from 'vue'

export default defineComponent({
  name: 'PersonalisedAmountCard',
  props: {
    customWalletOption: {
      type: Object as PropType<DepositBonusOption | null>,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    pendingAmount: {
      type: String,
      default: '',
    },
    minDepositPounds: {
      type: Number,
      required: true,
    },
    maxDepositPounds: {
      type: Number,
      required: true,
    },
    selectableAmounts: {
      type: Array as PropType<number[]>,
      default: () => [10, 25, 50, 100, 200],
    },
    isPaymentUpdating: {
      type: Boolean,
      default: false,
    },
    selectedDeposit: {
      type: Object as PropType<Deposit>,
      required: true,
    },
    depositAmounts: {
      type: Object as PropType<Map<string, DepositBonusOption>>,
      required: true,
    },
    depositOptionsWithCoinCount: {
      type: Array as PropType<
        Array<
          DepositBonusOption & {
            displayAmount: number
            bonusPercent: number
            coinCount: number
            silverCoinCount: number
          }
        >
      >,
      default: () => [],
    },
    minDepositAmount: {
      type: Number,
      required: true,
    },
    maxDepositAmount: {
      type: Number,
      required: true,
    },
    selectedCoinConfig: {
      type: Object as PropType<{
        totalCoins: number
        silverCoins: number
      } | null>,
      default: null,
    },
  },
  emits: ['select', 'update:pendingAmount', 'addValue'],
  setup() {
    return useCurrencyFormatter()
  },
  data() {
    return {
      GlpCoin,
      GlpCoinSilver,
    }
  },
  computed: {
    currentSelectedOption(): DepositBonusOption | null {
      if (!this.selectedDeposit?.sku) {
        return null
      }
      return this.depositAmounts.get(this.selectedDeposit.sku) ?? null
    },
    walletCoins(): { totalCoins: number; silverCoins: number } {
      // Use selected option's coin config when not selected (e.g., auto-selected to promo)
      if (!this.isSelected && this.selectedCoinConfig) {
        return this.selectedCoinConfig
      }

      const walletAmount = this.isSelected
        ? this.selectedDeposit.amount
        : parseInt(this.pendingAmount, 10) * 100

      if (!walletAmount || walletAmount < 1) {
        return { totalCoins: 1, silverCoins: 0 }
      }

      const bonusPercent = calculateBonusCredit(
        walletAmount,
        this.customWalletOption,
        this.depositAmounts
      )

      // Get option amounts for scaling consistency
      const optionAmounts = this.depositOptionsWithCoinCount.map(
        (opt) => opt.displayAmount
      )

      return calculateWalletCoins(
        walletAmount,
        bonusPercent,
        this.maxDepositAmount,
        optionAmounts
      )
    },
    bonusTierInfo() {
      if (!this.pendingAmount || parseInt(this.pendingAmount) <= 0) {
        return {
          percent: 0,
          nextTier: null,
          remainingPence: 0,
          nextBonusPercent: 0,
        }
      }
      return getBonusTierForAmount(
        parseInt(this.pendingAmount),
        this.customWalletOption
      )
    },
    currentBonusLabel(): string | null {
      const baseLabel = `+${this.$n(this.selectedDeposit.bonus_credit_percent, 'percent')} Credit`
      const option = this.currentSelectedOption

      if (!option || option.type !== 'promotion') {
        return this.selectedDeposit.bonus_credit_percent > 0 ? baseLabel : null
      }

      const maxBonusMinor = option.promotion_details?.max_bonus_minor
      if (maxBonusMinor && this.selectedDeposit.amount > maxBonusMinor) {
        return maxBonusMinor > 0
          ? `+${this.formatCurrencyFromPence(maxBonusMinor)} Credit`
          : null
      }

      return this.selectedDeposit.bonus_credit_percent > 0 ? baseLabel : null
    },
  },
  methods: {
    handleSelect() {
      this.$emit('select')
    },
    handleInput(value: string) {
      // Auto-select personalized option when editing the field
      if (!this.isSelected) {
        this.$emit('select')
      }
      // Strip any non-digit characters to ensure whole pounds only
      const sanitized = value.replace(/[^\d]/g, '')
      this.$emit('update:pendingAmount', sanitized)
    },
    handleAddValue(amount: number) {
      // Auto-select personalized option when using quick-add buttons
      if (!this.isSelected) {
        this.$emit('select')
      }
      this.$emit('addValue', amount)
    },
    blockInvalidInput(event: KeyboardEvent) {
      // Only block single-character keys that aren't digits
      if (event.key.length === 1 && !/^[0-9]$/.test(event.key)) {
        event.preventDefault()
      }
    },
  },
})
</script>

<template>
  <div
    v-if="customWalletOption"
    class="flex flex-col gap-3 rounded-lg p-3 text-center hover:cursor-pointer relative"
    :class="{
      'border-2 border-primary bg-primary-lighter': isSelected,
      'border m-[1px] border-slate-300': !isSelected,
    }"
    @click="handleSelect"
  >
    <p class="font-bold">Personalised Amount</p>

    <!-- Quick add buttons -->
    <div class="flex gap-1 justify-evenly">
      <button
        v-for="amount in selectableAmounts"
        :key="amount"
        type="button"
        class="amount-button"
        :class="[amount === 200 ? 'hidden md:flex' : 'flex']"
        @click.stop="handleAddValue(amount)"
      >
        +{{ amount }}
      </button>
    </div>

    <!-- Amount input -->
    <div
      class="relative flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2 bg-pure-white"
      :class="{ 'opacity-60 cursor-not-allowed': isPaymentUpdating }"
    >
      <span class="text-lg font-bold text-gray-600 select-none">
        {{ $t('currencySymbol') }}
      </span>
      <input
        :value="pendingAmount"
        name="customAmount"
        type="number"
        step="1"
        pattern="\d*"
        :min="minDepositPounds"
        :max="maxDepositPounds"
        :disabled="isPaymentUpdating"
        placeholder="Enter amount"
        class="w-full bg-transparent text-center text-lg font-bold outline-none border-none"
        inputmode="numeric"
        autocomplete="off"
        :aria-busy="isPaymentUpdating"
        @input="handleInput(($event.target as HTMLInputElement).value)"
        @keydown="blockInvalidInput"
        @click.stop
      />
    </div>

    <hr />

    <!-- Total row inside personalised card -->
    <div class="flex justify-center">
      Total:
      <span class="font-bold ml-1.5">
        {{ formatCurrencyFromPence(selectedDeposit.amount) }}
      </span>
      <template v-if="currentBonusLabel">
        <div class="vertical-line mx-2" />
        <span class="font-bold flex items-center text-blue-800">
          {{ currentBonusLabel }}
        </span>
      </template>
    </div>
  </div>
</template>

<style scoped>
.amount-button {
  @apply items-center justify-center text-sm bg-slate-100 p-2 flex-1 rounded-lg border border-slate-300 font-bold;
}
</style>
